import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { MODAL_NAMES } from '../../../Product-JP.constants'

import translations from './createCustomizationFunnelData.translations'
import type { Step } from './types'
import { mapStep } from './utils'

const STEP_IMAGES: Record<string, string> = {
  grades: '/img/product/jp/step-grades.png',
  battery: '/img/product/jp/step-battery.png',
  dual_sim: '/img/product/jp/step-sim.png',
  processor_type_and_speed: '/img/product/jp/step-processor.png',
  processor_type_and_graphic_card: '/img/product/jp/step-processor.png',
}

const STEP_LABEL: Record<string, I18nDefinition> = {
  grades: translations.gradesLabel,
  battery: translations.batteryLabel,
  dual_sim: translations.simLabel,
  processor_type_and_speed: translations.processorLabel,
  processor_type_and_graphic_card: translations.processorLabel,
}

const STEP_DESCRIPTION: Record<string, I18nDefinition> = {
  grades: translations.gradesDescrition,
  battery: translations.batteryDescrition,
  dual_sim: translations.simDescription,
  processor_type_and_speed: translations.processorDescription,
  processor_type_and_graphic_card: translations.processorDescription,
}

const STEP_MODALS: Record<string, string> = {
  grades: MODAL_NAMES.CONDITION,
  battery: MODAL_NAMES.ABOUT_BATTERY,
}

/**
 * This function is here to map the response of existing endpoints (pickers, best_offers) to
 * something easily usable by the Customization Funnel.
 *
 * We chose to reuse existing endpoints over a new dedicated one to gain some time developing the
 * MVP that will be A/B tested on smartphones only. This is not meant to be scaled to any other category
 * without a new endpoint to be created.
 */
export const createCustomizationFunnelData = (
  i18n: ReturnType<typeof useI18n>,
  pickers: GetPickersResponse['pickerGroups'],
): Step[] => {
  const steps: Array<Step | null> = pickers
    .map((group) => mapStep(group))
    // Override grades labels for test
    .map((group) => ({
      ...group,
      options: group.options.map((option) => ({
        ...option,
        label:
          group.id === 'grades'
            ? i18n(translations[`grade${option.trackingValue}`])
            : option.label,
      })),
    }))
    .map((group) => ({
      ...group,
      guidance: group.guidance
        ? {
            ...group.guidance,
            image: group.id in STEP_IMAGES ? STEP_IMAGES[group.id] : undefined,
            label:
              group.id in STEP_LABEL
                ? i18n(STEP_LABEL[group.id])
                : group.guidance.label,
            description:
              group.id in STEP_DESCRIPTION
                ? i18n(STEP_DESCRIPTION[group.id])
                : undefined,
            modalName:
              group.id in STEP_MODALS
                ? STEP_MODALS[group.id]
                : group.guidance?.modalName,
          }
        : undefined,
    }))
    .filter(Boolean)

  return steps as unknown as Step[]
}
